import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope, faPaperPlane, faFileLines,
  faTrash, faTrashCan, faArchive, faFolder,
  faChevronDown, faChevronRight, faGripVertical, faRss,
} from '@fortawesome/free-solid-svg-icons';

import {
  selectRootFolderIds,
  selectFolderById,
  selectChildFolderIds,
  selectIsFolderExpanded,
  selectFolderLoadStatus,
  toggleFolderExpanded,
  reorderRootFolders,
  reorderChildFolders,
} from '../../../Slices/mailFoldersSlice';
import { loadFolders } from '../../../Slices/Thunk/graphMailThunks';
import { fetchChildFolders } from '../../../Slices/mailFoldersSlice';
import { selectSelectedFolderId } from '../../../Slices/mailUiSlice';
import { selectFolderAndLoad } from '../../../Slices/Thunk/graphMailThunks';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FONT      = "'Segoe UI', Tahoma, sans-serif";
const DRAG_TYPE = 'MAIL_FOLDER';

const WELL_KNOWN_ICONS = {
  inbox:        faEnvelope,
  drafts:       faFileLines,
  sentitems:    faPaperPlane,
  deleteditems: faTrash,
  archive:      faArchive,
  junkemail:    faTrashCan,
  outbox:       faPaperPlane,
  rssfeeds:     faRss,
};

function folderIcon(wellKnownName) {
  return WELL_KNOWN_ICONS[wellKnownName] || faFolder;
}

// ---------------------------------------------------------------------------
// FolderRow
// ---------------------------------------------------------------------------

function FolderRow({ folderId, parentId = null, depth = 0 }) {
  const dispatch   = useDispatch();
  const folder     = useSelector((s) => selectFolderById(s, folderId));
  const childIds   = useSelector((s) => selectChildFolderIds(s, folderId));
  const isExpanded = useSelector((s) => selectIsFolderExpanded(s, folderId));
  const selectedId = useSelector(selectSelectedFolderId);

  // Drop indicator position ('before' | 'after' | null)
  const [dropIndicator, setDropIndicator] = useState(null);
  const dropPosRef = useRef(null);
  const rowRef     = useRef(null);

  // Derive early — used by drag/drop specs below; safe because hooks follow
  const isCustom = !folder?.wellKnownName;

  // ------------------------------------------------------------------
  // Drag — only enabled for custom (non-system) folders
  // All hooks must be called unconditionally before any early return
  // ------------------------------------------------------------------
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ id: folderId, parentId }),
    canDrag: () => isCustom,
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  });

  // ------------------------------------------------------------------
  // Drop — only accepts drags from the same parent level
  // ------------------------------------------------------------------
  const [{ isOver }, drop] = useDrop({
    accept: DRAG_TYPE,
    canDrop: (item) => isCustom && item.parentId === parentId && item.id !== folderId,
    hover: (item, monitor) => {
      if (!rowRef.current || item.id === folderId) return;
      const rect    = rowRef.current.getBoundingClientRect();
      const clientY = monitor.getClientOffset()?.y ?? 0;
      const newPos  = clientY < rect.top + rect.height / 2 ? 'before' : 'after';
      if (newPos !== dropPosRef.current) {
        dropPosRef.current = newPos;
        setDropIndicator(newPos);
      }
    },
    drop: (item) => {
      if (!isCustom || item.id === folderId) return;
      const before = dropPosRef.current === 'before';
      if (parentId === null) {
        dispatch(reorderRootFolders({ dragId: item.id, targetId: folderId, before }));
      } else {
        dispatch(reorderChildFolders({ parentId, dragId: item.id, targetId: folderId, before }));
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
  });

  // Clear indicator when drag leaves this row
  useEffect(() => {
    if (!isOver) {
      dropPosRef.current = null;
      setDropIndicator(null);
    }
  }, [isOver]);

  // Combine drag preview + drop on the row; drag handle is separate
  drop(rowRef);
  dragPreview(rowRef);

  // Early return AFTER all hooks
  if (!folder) return null;

  const isActive    = selectedId === folderId;
  const hasChildren = (folder.childFolderCount || 0) > 0 || childIds.length > 0;
  const unread      = folder.unreadItemCount || 0;
  const icon        = folderIcon(folder.wellKnownName);

  function handleClick() {
    dispatch(selectFolderAndLoad({ folderId }));
  }

  function handleChevronClick(e) {
    e.stopPropagation();
    dispatch(toggleFolderExpanded({ folderId }));
    if (!isExpanded && childIds.length === 0 && folder.childFolderCount > 0) {
      dispatch(fetchChildFolders({ parentId: folderId }));
    }
  }

  return (
    <>
      <div
        ref={rowRef}
        onClick={handleClick}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          paddingLeft: 14 + depth * 16,
          paddingRight: 10,
          height: 36,
          cursor: isCustom ? 'pointer' : 'pointer',
          userSelect: 'none',
          borderRadius: '0 20px 20px 0',
          marginRight: 8,
          background: isActive ? '#deecf9' : 'transparent',
          color: isActive ? '#0f6cbd' : '#323130',
          fontWeight: isActive ? 600 : 400,
          fontFamily: FONT,
          fontSize: 14,
          transition: 'background 0.1s',
          flexShrink: 0,
          opacity: isDragging ? 0.35 : 1,
          // Drop indicator lines — absolute positioned so they don't affect layout
        }}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f3f2f1'; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Drop indicator — top */}
        {dropIndicator === 'before' && (
          <div style={{
            position: 'absolute', top: 0, left: 14 + depth * 16, right: 8,
            height: 2, background: '#0f6cbd', borderRadius: 1, zIndex: 10, pointerEvents: 'none',
          }} />
        )}

        {/* Drop indicator — bottom */}
        {dropIndicator === 'after' && (
          <div style={{
            position: 'absolute', bottom: 0, left: 14 + depth * 16, right: 8,
            height: 2, background: '#0f6cbd', borderRadius: 1, zIndex: 10, pointerEvents: 'none',
          }} />
        )}

        {/* Drag handle — only visible on custom folders, shown on hover via CSS class */}
        {isCustom && (
          <span
            ref={drag}
            className="folder-drag-handle"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: depth * 16,
              width: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              cursor: 'grab',
              color: '#a19f9d',
              fontSize: 10,
              transition: 'opacity 0.1s',
            }}
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </span>
        )}

        {/* Expand chevron */}
        <span
          onClick={hasChildren ? handleChevronClick : undefined}
          style={{
            width: 12, flexShrink: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: hasChildren ? 'pointer' : 'default',
          }}
        >
          {hasChildren && (
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
              style={{ fontSize: 9, color: '#605e5c' }}
            />
          )}
        </span>

        {/* Folder icon */}
        <FontAwesomeIcon
          icon={icon}
          style={{ width: 16, fontSize: 14, color: isActive ? '#0f6cbd' : '#605e5c', flexShrink: 0 }}
        />

        {/* Name */}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {folder.displayName}
        </span>

        {/* Unread badge */}
        {unread > 0 && (
          <span style={{
            background: '#0f6cbd', color: '#fff', fontSize: 11, fontWeight: 700,
            minWidth: 18, height: 18, borderRadius: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', flexShrink: 0,
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </div>

      {/* Children */}
      {isExpanded && childIds.map((cid) => (
        <FolderRow key={cid} folderId={cid} parentId={folderId} depth={depth + 1} />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// MailFolderSidebar
// ---------------------------------------------------------------------------

export default function MailFolderSidebar({ userEmail }) {
  const dispatch   = useDispatch();
  const rootIds    = useSelector(selectRootFolderIds);
  const loadStatus = useSelector(selectFolderLoadStatus);

  useEffect(() => {
    if (loadStatus === 'idle') {
      dispatch(loadFolders());
    }
  }, [dispatch, loadStatus]);

  return (
    <DndProvider backend={HTML5Backend}>
      <style>{dragCss}</style>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#fff',
        overflowY: 'auto',
        overflowX: 'hidden',
        fontFamily: FONT,
      }}>
        {/* Account label */}
        {userEmail && (
          <div style={{
            padding: '10px 14px 6px',
            fontSize: 11,
            color: '#8a8886',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            {userEmail}
          </div>
        )}

        {/* Loading state */}
        {loadStatus === 'loading' && rootIds.length === 0 && (
          <div style={{ padding: '16px 14px', color: '#8a8886', fontSize: 13 }}>
            Loading folders…
          </div>
        )}

        {/* Error state */}
        {loadStatus === 'error' && (
          <div style={{ padding: '16px 14px', color: '#c50f1f', fontSize: 13 }}>
            Failed to load folders.{' '}
            <span
              style={{ color: '#0f6cbd', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => dispatch(loadFolders())}
            >
              Retry
            </span>
          </div>
        )}

        {/* Folder tree */}
        <div style={{ flex: 1, paddingBottom: 8 }}>
          {rootIds.map((id) => (
            <FolderRow key={id} folderId={id} parentId={null} depth={0} />
          ))}
        </div>
      </div>
    </DndProvider>
  );
}

// ---------------------------------------------------------------------------
// CSS — drag handle appears on row hover; grabbing cursor during drag
// ---------------------------------------------------------------------------

const dragCss = `
  div:hover > .folder-drag-handle {
    opacity: 1 !important;
  }
  .folder-drag-handle:active {
    cursor: grabbing !important;
  }
`;
